import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from "@tanstack/react-query";
import { useUserContext } from '../../context/user';
import useExportData from '../../hooks/useExportData';
import { useSorting } from '../../hooks/useSorting';
import { MetricType } from '../../types/api/metrics/metricsType';
import { ActionType, Hint, MetricRequest, ScreenName } from '../../types/api/metrics/request';
import { convertToSnakeCase } from '../../helpers/strings';
import { toYearMonthFormat, toDateFormat } from '../../helpers/dates';
import useCleanPaginationURL from '../../hooks/useCleanPaginationURL';
import { usePaginationTextReplace } from '../../hooks/usePaginationTextReplace';
import { exportMetric } from '../../services/metrics';
import { createDataTableColumns } from '../common/TableComponent';
import { Placeholder, createErrorPlaceholder } from '../basic';
import { pageSize } from '../../services/settings';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import { useAlert } from '../../context/alert';
import { MetricTypeSelect } from './components/MetricTypeSelect';
import { Stack } from '@primer/react'
import { metricsTableConfig, TableColumn } from '../common/TableComponent';
import { AvailableMetricResponse } from '../../types/api/metrics/request';
import { getMetricFilterConfig } from './MetricList+Filter';
import { useAvailableMetricsQuery } from '../../hooks/useMetricsQuery';
import { fetchMetric } from '../../services/metrics';
import { columnsForCurrentMetrics, emptyListDescription } from './MetricList+Data';
import { MetricFilterModal } from './components/MetricFilterModal';
import { FilterValues, FilterCallbacks, FilterConfig } from '../../types/common/filters';
import { Button } from '@primer/react';
import { Table, DataTable, SkeletonBox } from '@primer/react/experimental'
import { UploadIcon, XCircleIcon } from '@primer/octicons-react'
import { CatalogOptionResponse } from '../../types/api/ads';
import { TimeOfDay } from '../../types/api/metrics/tables/watchAllAds';

export const INITIAL_FILTER_VALUES: FilterValues = {
    platform: null,
    status: null,
    category: null,
    countryResponseForName: null,
    cityResponseForName: null,
    countryResponseForId: null,
    cityResponseForId: null,
    marketingCompany: null,
    trafficSource: null,
    subcategory: null,
    buttonName: null,
    userType: null,
    withHint: null,
    withoutHint: null,
    region: null,
    sourceScreen: null,
    targetScreen: null,
    timeOfDay: null,
    contactType: null,
    filterName: null,
    screen: null,
    hint: null,
    actionType: null,
    optionTitle: null,
    price: { from: '', to: '' },
    date: { from: '', to: '' },
    time: { from: '', to: '' },
    scoringUserType: null
};

export default function MetricList() {

    const { profile } = useUserContext();
    const navigate = useNavigate();

    const [pageIndex, setPageIndex] = React.useState(1)

    const queryClient = useQueryClient();

    const skipNextFetchRef = useRef(false);
    const { isLoading: isLoadingExport, exportAction } = useExportData();
    const { setSuccess, setInfo, setError, addCustomAlert, updateAlert, removeAlert } = useAlert();
    // Fetch tables list
    const [selectedMetric, setSelectedMetric] = useState<AvailableMetricResponse | undefined>(undefined);
    const [columnsStructure, setColumnsStructure] = useState<TableColumn<any>[]>([]);
    const [filterConfig, setFilterConfig] = useState<FilterConfig | undefined>(undefined);
    const isMetricJustChanged = useRef(false);
    const [filterParams, setFilterParams] = useState<MetricRequest | undefined>(undefined);

    // Fetch data
    const [isLoadingMetricData, setIsLoadingMetricData] = useState(false);
    const [metricError, setMetricError] = useState<Error | null>(null);
    const [isMetricError, setIsMetricError] = useState(false);
    const [isFetchedMetricData, setIsFetchedMetricData] = useState(false);
    const [metricData, setMetricData] = useState<any>(null);

    const {
        orderingParam,
        handleSort,
        getSortIcon,
        getSortText
    } = useSorting<any>('');

    const {
        isLoading: loadingMetricSections,
        data: metricSections,
    } = useAvailableMetricsQuery();

    const paramsForMetricFilters = useCallback(() => {
        if (isMetricJustChanged.current) {
            isMetricJustChanged.current = false;
        }

        const params = createMetricRequestParam()

        return params
    }, [pageSize, pageIndex, orderingParam, filterParams])

    // Состояния для фильтров
    const [filterOpened, setFilterOpened] = useState(false);
    const [priceFilterOpened, setPriceFilterOpened] = useState(false);
    const [dateFilterOpened, setDateFilterOpened] = useState(false);
    const [selectFilterOpened, setSelectFilterOpened] = useState<string | null>(null);
    const [detailedSelectFilterOpened, setDetailedSelectFilterOpened] = useState<string | null>(null);

    const [filterValues, setFilterValues] = useState<FilterValues>(INITIAL_FILTER_VALUES);

    useEffect(() => {
        const metrics = metricSections?.availableMetrics
        if (metrics && metrics.length > 0) {
            setSelectedMetric(metricSections.availableMetrics[0])
        }
    }, [metricSections]);

    useEffect(() => {
        setFilterValues(INITIAL_FILTER_VALUES);
        setFilterParams(undefined);
        setPageIndex(1);
        handleSort('');
    }, [selectedMetric?.metricName, selectedMetric?.metricType]);

    const prevMetricRef = useRef<string | null>(null);

    useEffect(() => {
        if (!selectedMetric?.metricName || !selectedMetric?.metricType) {
            return;
        }

        const currentMetricKey = `${selectedMetric.metricName}-${selectedMetric.metricType}`;
        const metricHasChanged = prevMetricRef.current !== null && prevMetricRef.current !== currentMetricKey;

        // Если метрика изменилась
        if (metricHasChanged) {
            prevMetricRef.current = currentMetricKey;

            // Ждем пока filterParams не станет undefined, пропускаем этот запрос
            if (filterParams !== undefined) {
                return;
            }

            setIsLoadingMetricData(true);
            setIsFetchedMetricData(false);

            const params = {
                limit: pageSize,
                offset: 0,
                order_by: undefined
            };

            const fetchData = async () => {
                setIsMetricError(false);
                setMetricError(null);

                try {
                    const data = await fetchMetric(
                        selectedMetric.metricName,
                        selectedMetric.metricType,
                        params
                    );
                    setMetricData(data);
                    setIsFetchedMetricData(true);
                } catch (error) {
                    setMetricError(error as Error);
                    setIsMetricError(true);
                } finally {
                    setIsLoadingMetricData(false);
                }
            };

            fetchData();
            return;
        }

        prevMetricRef.current = currentMetricKey;

        // Обычный запрос с фильтрами
        setIsLoadingMetricData(true);
        setIsFetchedMetricData(false);

        const params = filterParams ? {
            ...filterParams,
            order_by: (orderingParam.length > 0 ? convertToSnakeCase(orderingParam) : undefined)
        } : createMetricRequestParam();

        const fetchData = async () => {
            setIsMetricError(false);
            setMetricError(null);

            try {
                const data = await fetchMetric(
                    selectedMetric.metricName,
                    selectedMetric.metricType,
                    params
                );
                setMetricData(data);
                setIsFetchedMetricData(true);
            } catch (error) {
                setMetricError(error as Error);
                setIsMetricError(true);
            } finally {
                setIsLoadingMetricData(false);
            }
        };

        fetchData();
    }, [
        selectedMetric?.metricName,
        selectedMetric?.metricType,
        orderingParam,
        pageIndex,
        filterParams
    ]);

    const filterCallbacks: FilterCallbacks = {
        onFilterChange: (key, value) => {
            setFilterValues(prev => ({
                ...prev,
                [key]: value
            }));
        },
        onPriceChange: (from, to) => {
            setFilterValues(prev => ({
                ...prev,
                price: { from, to }
            }));
        },
        onDateChange: (from, to) => {
            setFilterValues(prev => ({
                ...prev,
                date: { from, to }
            }));
        }
    };

    const handleDismiss = () => {
        setFilterValues(INITIAL_FILTER_VALUES);
        setFilterParams(undefined);
    };

    const createMetricRequestParam = () => {
        const createdFromIsoDate = filterValues.date?.from ? toDateFormat(filterValues.date?.from) : undefined;
        const createdToIsoDate = filterValues.date?.to ? toDateFormat(filterValues.date?.to) : undefined;

        const createdFromIsoMonthYearDate = filterValues.date?.from ? toYearMonthFormat(filterValues.date?.from) : undefined;
        const createdToIsoMonthYearDate = filterValues.date?.to ? toYearMonthFormat(filterValues.date?.to) : undefined;

        const hasDateFilterMode = filterConfig?.dateFilter?.mode !== undefined;
        const isMonthYearMode = filterConfig?.dateFilter?.mode === 'monthYear';
        const isFullMode = filterConfig?.dateFilter?.mode === 'full';

        const cityId = () => {
            if (selectedMetric) {
                switch (selectedMetric.metricName) {
                    case MetricType.phoneMasks:
                    case MetricType.ipGeoposition:
                    case MetricType.geopositionComposite:
                        return filterValues.cityResponseForId?.map(el => el.id)
                    default:
                        return undefined
                }
            }
            return undefined
        }

        const cityName = () => {
            if (selectedMetric) {
                switch (selectedMetric.metricName) {
                    case MetricType.phoneMasks:
                    case MetricType.ipGeoposition:
                    case MetricType.geopositionComposite:
                        return undefined
                    default:
                        return filterValues.cityResponseForName?.map(el => el.value)
                }
            }
            return undefined
        }

        const countryId = () => {
            if (selectedMetric) {
                switch (selectedMetric.metricName) {
                    case MetricType.phoneMasks:
                    case MetricType.ipGeoposition:
                    case MetricType.geopositionComposite:
                        return filterValues.countryResponseForId?.map(el => el.id)
                    default:
                        return undefined
                }
            }
            return undefined
        }

        const countryName = () => {
            if (selectedMetric) {
                switch (selectedMetric.metricName) {
                    case MetricType.phoneMasks:
                    case MetricType.ipGeoposition:
                    case MetricType.geopositionComposite:
                        return undefined
                    default:
                        return filterValues.countryResponseForName?.map(el => el.value)
                }
            }
            return undefined
        }

        const hint = () => {
            const status = filterValues.hint as Hint | null;
            if (status) {
                return status === 'withHint';
            }
            return undefined;
        }

        const userType = () => {
            const type = filterValues.userType;
            if (type) {
                return [type];
            }
            return [];
        }

        const params: MetricRequest = {
            button_name: filterValues.buttonName?.map(el => el),
            catalog_id: filterValues.subcategory?.map(el => el.id),
            category_id: filterValues.category?.map(el => el.id),
            city: cityName(),
            city_id: cityId(),
            contact_method: filterValues.contactType,
            country: countryName(),
            country_id: countryId(),
            date_from: (hasDateFilterMode && isFullMode) ? createdFromIsoDate : undefined,
            date_to: (hasDateFilterMode && isFullMode) ? createdToIsoDate : undefined,
            limit: pageSize,
            month_from: (hasDateFilterMode && isMonthYearMode) ? createdFromIsoMonthYearDate : undefined,
            month_to: (hasDateFilterMode && isMonthYearMode) ? createdToIsoMonthYearDate : undefined,
            offset: pageIndex - 1,
            option_title: filterValues.filterName?.map((el: CatalogOptionResponse) => el.id),
            order_by: (orderingParam.length > 0 ? convertToSnakeCase(orderingParam) : undefined),
            platform: filterValues.platform?.map(el => el),
            screen_name: filterValues.screen?.map((el: ScreenName) => el),
            time_of_day: filterValues.timeOfDay?.map((el: TimeOfDay) => el),
            used_hint: hint(),
            user_type: userType(),
            fromScreenName: filterValues.sourceScreen?.map((el: ScreenName) => el),
            toScreenName: filterValues.targetScreen?.map((el: ScreenName) => el),
            actionType: filterValues.actionType?.map((el: ActionType) => el),
        };

        console.log('createMetricRequestParam params', params)

        return params
    }

    const handleApply = () => {
        const params = createMetricRequestParam();
        setFilterParams(params);
        setPageIndex(1);
    };

    useCleanPaginationURL();
    usePaginationTextReplace()

    // EXPORT METRIC
    const downloadMetrics = async () => {
        try {
            const method = () => exportMetric(
                selectedMetric?.metricName || "",
                selectedMetric?.metricType || "",
                {
                    ...paramsForMetricFilters(),
                    limit: undefined,
                    offset: undefined
                }
            )
            const result = await exportAction(method, "metrics", "xlsx");
            if (result.success) {
                setSuccess('Загрузка метрики успешно началась');
            }
        } catch (err) { }
    }

    useEffect(() => {
        queryClient.invalidateQueries({ queryKey: [pageIndex] })
    }, [pageIndex]);

    const dataWithIds = (metricData?.list.data ?? []).map((item: any, index: number) => ({
        ...item,
        id: index.toString()
    }));

    const columns = createDataTableColumns(
        columnsStructure,
        handleSort,
        getSortIcon,
        getSortText
    );

    useEffect(() => {
        if (selectedMetric) {
            handleSort('')
            let structure = columnsForCurrentMetrics(selectedMetric)
            setColumnsStructure(structure as TableColumn<any>[])
            let filter = getMetricFilterConfig(selectedMetric)
            setFilterConfig(filter)
            setPageIndex(1)
        }
    }, [selectedMetric, handleSort]);

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    {loadingMetricSections && <SkeletonBox width={400} height={32} />}
                    {metricSections && <MetricTypeSelect
                        metrics={metricSections.availableMetrics}
                        value={selectedMetric}
                        onChange={(val) => {
                            isMetricJustChanged.current = true;
                            handleSort('');
                            setSelectedMetric(val)
                        }
                        }
                    />}
                    <Stack direction="horizontal">
                        <Button
                            leadingVisual={UploadIcon}
                            loading={isLoadingExport}
                            disabled={isLoadingMetricData}
                            variant="default"
                            onClick={downloadMetrics}
                        >
                            Сформировать отчет
                        </Button>
                        {filterConfig && <MetricFilterModal
                            key={`${selectedMetric?.metricName}-${selectedMetric?.metricType}`}
                            filterOpened={filterOpened}
                            setFilterOpened={setFilterOpened}
                            priceFilterOpened={priceFilterOpened}
                            setPriceFilterOpened={setPriceFilterOpened}
                            dateFilterOpened={dateFilterOpened}
                            setDateFilterOpened={setDateFilterOpened}
                            selectFilterOpened={selectFilterOpened}
                            setSelectFilterOpened={setSelectFilterOpened}
                            detailedSelectFilterOpened={detailedSelectFilterOpened}
                            setDetailedSelectFilterOpened={setDetailedSelectFilterOpened}
                            config={filterConfig}
                            values={filterValues}
                            callbacks={filterCallbacks}
                            onDismiss={handleDismiss}
                            onApply={handleApply}
                        />}
                    </Stack>
                </Stack>
            }
            content={
                <Stack>
                    {isLoadingMetricData && <Table.Container>
                        <Table.Skeleton
                            aria-labelledby="metrics-loading"
                            rows={pageSize}
                            columns={metricsTableConfig(7).map((el) => {
                                return {
                                    header: el.header,
                                    id: el.field,
                                }
                            })}
                        />
                    </Table.Container>}
                    {isFetchedMetricData && !isLoadingMetricData && metricData?.list.data.length === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Ничего не было найдено'
                            description={emptyListDescription}
                            button={undefined}
                        />
                    )}
                    {isFetchedMetricData && metricData && metricData.list.data.length > 0 &&
                        <Table.Container>
                            <DataTable
                                aria-labelledby="metrics-table"
                                data={dataWithIds}
                                columns={columns}
                            />
                        </Table.Container>
                    }
                    {isFetchedMetricData && metricData && metricData.list.data.length > 0 &&
                        (<div className="sticky bottom-0 bg-white border-t border-gray-200 z-10 -mt-4">
                            <Table.Pagination aria-label="pagination" pageSize={pageSize} totalCount={metricData.totalCount} onChange={({
                                pageIndex
                            }) => {
                                setPageIndex(pageIndex + 1);
                            }} defaultPageIndex={pageIndex - 1} />
                        </div>
                        )}
                </Stack>
            }
        ></RegularPageLayout>

    )
}
